import React, {useState, useEffect} from 'react'
import {Modal, Button} from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import {KTSVG} from '../../../../helpers'

type Props = {
  show: boolean
  handleClose: () => void
  onSave: (recurrenceData: any) => void
  initialData?: any
}

const frequencyOptions = [
  {value: 'daily', label: 'Diário (todos os dias)'},
  {value: 'weekdays', label: 'Diária (Seg a Sex)'},
  {value: 'weekly_custom_days', label: 'Dias da semana selecionados'},
  {value: 'weekly', label: 'Semanal'},
  {value: 'biweekly', label: 'Bissemanal'},
  {value: 'monthly', label: 'Mensal'},
  {value: 'bimonthly', label: 'Bimestral'},
  {value: 'quarterly', label: 'Trimestral'},
  {value: 'semiannual', label: 'Semestral'},
  {value: 'annual', label: 'Anual'},
]

const RuleTypeOptions = [
  {value: 'same_day', label: 'Mesma data da recorrência'},
  {value: 'first_day', label: 'Primeiro dia do período'},
  {value: 'custom_weekday', label: 'Regra customizada'},
]

const weekDays = [
  {value: 1, label: 'Seg', fullLabel: 'Segunda-feira'},
  {value: 2, label: 'Ter', fullLabel: 'Terça-feira'},
  {value: 3, label: 'Qua', fullLabel: 'Quarta-feira'},
  {value: 4, label: 'Qui', fullLabel: 'Quinta-feira'},
  {value: 5, label: 'Sex', fullLabel: 'Sexta-feira'},
  {value: 6, label: 'Sáb', fullLabel: 'Sábado'},
  {value: 0, label: 'Dom', fullLabel: 'Domingo'},
]

const availableRelations = [
  {key: 'tags', label: 'Tags'},
  {key: 'task_responsible', label: 'Responsável'},
  {key: 'taskChecklists', label: 'Checklists'},
  {key: 'taskAttachement', label: 'Anexos'},
  {key: 'subtasks', label: 'Subtarefas'},
]

const TaskRecurrenceModal: React.FC<Props> = ({show, handleClose, onSave, initialData}) => {
  const [isRecurring, setIsRecurring] = useState(false)
  const [frequency, setFrequency] = useState<string>('')
  const [endType, setEndType] = useState<'never' | 'count' | 'until_date'>('never')
  const [repeatCount, setRepeatCount] = useState<number>(1)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([])
  const [ruleType, setRuleType] = useState<string>('same_day')
  const [ruleDays, setRuleDays] = useState({weekday: 1, position: '1', month: '1'})
  const [replicationRelations, setReplicationRelations] = useState<string[]>([])
  const [replicateOnDelivered, setReplicateOnDelivered] = useState(false)

  useEffect(() => {
    if (show && initialData) {
      setIsRecurring(true)
      setFrequency(initialData.frequency || '')
      
      let mappedEndType: 'never' | 'count' | 'until_date' = 'never'
      if (initialData.endType) {
        mappedEndType = initialData.endType === 'count' ? 'count' : 
                        initialData.endType === 'until_date' ? 'until_date' : 'never'
      } else if (initialData.recurrence_type) {
        mappedEndType = initialData.recurrence_type === 'count' ? 'count' : 
                        initialData.recurrence_type === 'until_date' ? 'until_date' : 'never'
      }
      setEndType(mappedEndType)
      
      setRepeatCount(initialData.repeatCount ?? initialData.repeat_count ?? 1)
      
      const endDateValue = initialData.endDate || initialData.end_date
      setEndDate(endDateValue ? new Date(endDateValue) : null)
      
      setSelectedWeekdays(initialData.selectedWeekdays || initialData.weekdays || [])
      
      setRuleType(initialData.ruleType || initialData.rule_type || 'same_day')
      
      setRuleDays(initialData.ruleDays || initialData.rule_days || {weekday: 1, position: '1', month: '1'})

      const relations = initialData.replicationRelations || initialData.replication_relations || []
      let normalizedRelations = [...relations]
      if (normalizedRelations.includes('task_responsible') && !normalizedRelations.includes('sequenceResponsibles')) {
        normalizedRelations.push('sequenceResponsibles')
      }
      setReplicationRelations(normalizedRelations)
      
      setReplicateOnDelivered(initialData.replicateOnDelivered ?? initialData.replicate_on_delivered ?? false)
    } else if (show && !initialData) {
      setIsRecurring(false)
      setFrequency('')
      setEndType('never')
      setRepeatCount(1)
      setEndDate(null)
      setSelectedWeekdays([])
      setRuleType('same_day')
      setRuleDays({weekday: 1, position: '1', month: '1'})
      setReplicationRelations([])
      setReplicateOnDelivered(false)
    }
  }, [show, initialData])

  const handleWeekdayToggle = (day: number) => {
    const newSelectedDays = selectedWeekdays.includes(day)
      ? selectedWeekdays.filter((d) => d !== day)
      : [...selectedWeekdays, day]
    setSelectedWeekdays(newSelectedDays)
  }

  const handleSave = () => {
    if (!isRecurring) {
      onSave(null)
      handleClose()
      return
    }

    const recurrence = {
      frequency,
      endType,
      repeatCount: endType === 'count' ? repeatCount : null,
      endDate: endType === 'until_date' && endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
      selectedWeekdays: frequency === 'weekly_custom_days' ? selectedWeekdays : null,
      ruleType: ruleType ?? null,
      ruleDays: ruleType == 'custom_weekday' ? ruleDays : null,
      replication_relations: replicationRelations,
      replicate_on_delivered: replicateOnDelivered,
    }

    onSave(recurrence)
    handleClose()
  }

  const handleCancel = () => {
    handleClose()
  }

  return (
    <>
      {show && <div className='task-recurrence-modal-overlay' />}
      <Modal
        show={show}
        onHide={handleCancel}
        centered
        backdrop={false}
        dialogClassName='modal-dialog-scrollable'
        className='task-recurrence-modal task-modal-content'
      >
        <Modal.Header className='border-0 pb-0'>
          <Modal.Title className='d-flex align-items-center'>
            <i className='bi bi-arrow-repeat text-primary fs-2 me-3' />
            <span>Configurar Recorrência</span>
          </Modal.Title>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={handleCancel}
          >
            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
          </button>
        </Modal.Header>

        <Modal.Body className='pt-5'>
          <div className='mb-5'>
            <div className='alert alert-light-info d-flex align-items-start p-4 mb-4'>
              <i className='bi bi-info-circle fs-2 me-3 text-primary'></i>
              <div>
                <div className='fw-bold text-dark mb-2'>Como funciona a recorrência:</div>
                <ul className='mb-0 text-muted fs-6'>
                  <li>A nova tarefa manterá a descrição e configurações originais</li>
                  <li>Histórico e comentários não serão copiados</li>
                  <li>As datas serão ajustadas conforme a frequência escolhida</li>
                  <li>A nova data de vencimento das novas tarefas será nula</li>
                </ul>
              </div>
            </div>
          </div>

          <div className='form-check form-switch mb-6'>
            <input
              className='form-check-input'
              type='checkbox'
              id='recurrenceSwitch'
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
            />
            <label className='form-check-label fw-semibold fs-5' htmlFor='recurrenceSwitch'>
              Ativar recorrência
            </label>
          </div>

          {isRecurring && (
            <div className='recurrence-options'>
              <div className='mb-6'>
                <label className='form-label fw-bold required'>Frequência</label>
                <select
                  className='form-select form-select-solid'
                  value={frequency}
                  onChange={(e) => {
                    setFrequency(e.target.value)
                    if (e.target.value !== 'weekly_custom_days') {
                      setSelectedWeekdays([])
                    }
                  }}
                >
                  <option value=''>Selecione a frequência</option>
                  {frequencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {frequency === 'weekly_custom_days' && (
                <div className='mb-6'>
                  <label className='form-label fw-bold required'>Dias da semana</label>
                  <div className='d-flex flex-wrap gap-2'>
                    {weekDays.map((day) => (
                      <button
                        key={day.value}
                        type='button'
                        className={`btn btn-sm ${
                          selectedWeekdays.includes(day.value) ? 'btn-primary' : 'btn-light-primary'
                        }`}
                        onClick={() => handleWeekdayToggle(day.value)}
                        title={day.fullLabel}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  {selectedWeekdays.length === 0 && (
                    <div className='text-danger mt-2 fs-7'>
                      Selecione pelo menos um dia da semana
                    </div>
                  )}
                </div>
              )}

              {['monthly', 'bimonthly', 'quarterly', 'semiannual', 'annual'].includes(
                frequency
              ) && (
                <div className='mb-6'>
                  <label className='form-label fw-bold'>Tipo de recorrência mensal</label>
                  <select
                    className='form-select form-select-solid'
                    value={ruleType}
                    onChange={(e) => setRuleType(e.target.value)}
                  >
                    {RuleTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {ruleType === 'custom_weekday' && (
                    <div className='mt-4'>
                      <label className='form-label fw-bold mb-3'>
                        Configurar regra personalizada
                      </label>
                      <div className='row g-3'>
                        <div className='col-md-4'>
                          <label className='form-label'>Semana do mês</label>
                          <select
                            className='form-select form-select-solid'
                            value={ruleDays.position}
                            onChange={(e) => setRuleDays({...ruleDays, position: e.target.value})}
                          >
                            <option value='1'>1ª semana</option>
                            <option value='2'>2ª semana</option>
                            <option value='3'>3ª semana</option>
                            <option value='4'>4ª semana</option>
                            <option value='last'>Última semana</option>
                          </select>
                        </div>
                        <div className='col-md-4'>
                          <label className='form-label'>Dia da semana</label>
                          <select
                            className='form-select form-select-solid'
                            value={ruleDays.weekday}
                            onChange={(e) =>
                              setRuleDays({...ruleDays, weekday: Number(e.target.value)})
                            }
                          >
                            <option value='0'>Domingo</option>
                            <option value='1'>Segunda-feira</option>
                            <option value='2'>Terça-feira</option>
                            <option value='3'>Quarta-feira</option>
                            <option value='4'>Quinta-feira</option>
                            <option value='5'>Sexta-feira</option>
                            <option value='6'>Sábado</option>
                          </select>
                        </div>
                        {frequency == 'annual' && (
                          <div className='col-md-4'>
                            <label className='form-label'>Mês</label>
                            <select
                              className='form-select'
                              value={ruleDays.month}
                              onChange={(e) => setRuleDays({...ruleDays, month: e.target.value})}
                            >
                              <option value='1'>Janeiro</option>
                              <option value='2'>Fevereiro</option>
                              <option value='3'>Março</option>
                              <option value='4'>Abril</option>
                              <option value='5'>Maio</option>
                              <option value='6'>Junho</option>
                              <option value='7'>Julho</option>
                              <option value='8'>Agosto</option>
                              <option value='9'>Setembro</option>
                              <option value='10'>Outubro</option>
                              <option value='11'>Novembro</option>
                              <option value='12'>Dezembro</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {frequency && (
                <>
                  <div className='form-check form-switch mt-6 mb-6'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='replicateOnDeliveredSwitch'
                      checked={replicateOnDelivered}
                      onChange={(e) => setReplicateOnDelivered(e.target.checked)}
                    />
                    <label
                      className='form-check-label fw-semibold fs-6'
                      htmlFor='replicateOnDeliveredSwitch'
                    >
                      Recriar tarefa automaticamente ao entregar
                    </label>
                  </div>

                  <div className='mb-6'>
                    <label className='form-label fw-bold'>Término da recorrência</label>
                    <div className='d-flex flex-column gap-4'>
                      <div className='form-check d-flex align-items-center'>
                        <input
                          className='form-check-input me-3'
                          type='radio'
                          name='endType'
                          id='endNever'
                          value='never'
                          checked={endType === 'never'}
                          onChange={(e) => setEndType('never')}
                        />
                        <label className='form-check-label' htmlFor='endNever'>
                          Nunca (recorrência indefinida)
                        </label>
                      </div>

                      <div className='form-check d-flex align-items-center'>
                        <input
                          className='form-check-input me-3'
                          type='radio'
                          name='endType'
                          id='endCount'
                          value='count'
                          checked={endType === 'count'}
                          onChange={(e) => setEndType('count')}
                        />
                        <label
                          className='form-check-label d-flex align-items-center gap-3'
                          htmlFor='endCount'
                        >
                          <span>Após</span>
                          <input
                            type='number'
                            className='form-control form-control-solid recurrence-number-input'
                            value={repeatCount}
                            onChange={(e) => setRepeatCount(Number(e.target.value))}
                            min='1'
                            disabled={endType !== 'count'}
                          />
                          <span>ocorrências</span>
                        </label>
                      </div>

                      <div className='form-check d-flex align-items-center'>
                        <input
                          className='form-check-input me-3'
                          type='radio'
                          name='endType'
                          id='endDate'
                          value='date'
                          checked={endType === 'until_date'}
                          onChange={(e) => setEndType('until_date')}
                        />
                        <label
                          className='form-check-label d-flex align-items-center gap-3'
                          htmlFor='endDate'
                        >
                          <span>Em</span>
                          <div className='recurrence-datepicker-wrapper'>
                            <DatePicker
                              selected={endDate}
                              onChange={(date) => setEndDate(date)}
                              placeholderText='Selecione a data'
                              className='form-control form-control-solid'
                              dateFormat='dd/MM/yyyy'
                              minDate={new Date()}
                              disabled={endType !== 'until_date'}
                              locale='pt-BR'
                            />
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className='mb-6'>
                    <label className='form-label fw-bold'>Relacionamentos a manter</label>
                    <div className='d-flex flex-column gap-3'>
                      {availableRelations.map((relation) => (
                        <div key={relation.key} className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id={`relation-${relation.key}`}
                            checked={replicationRelations.includes(relation.key)}
                            onChange={(e) => {
                              const updated = e.target.checked
                                ? relation.key == 'task_responsible'
                                  ? [...replicationRelations, relation.key, 'sequenceResponsibles']
                                  : [...replicationRelations, relation.key]
                                : replicationRelations.filter((r) =>
                                    relation.key == 'task_responsible'
                                      ? r !== relation.key && r !== 'sequenceResponsibles'
                                      : r !== relation.key
                                  )

                              setReplicationRelations(updated)
                            }}
                          />
                          <label className='form-check-label' htmlFor={`relation-${relation.key}`}>
                            {relation.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className='border-0'>
          <Button variant='light' onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            onClick={handleSave}
            disabled={
              isRecurring &&
              (!frequency || (frequency === 'weekly_custom_days' && selectedWeekdays.length === 0))
            }
          >
            Salvar Configuração
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {TaskRecurrenceModal}
